import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, Database, Settings, CheckCircle, AlertCircle, X, Download } from 'lucide-react';
import FileUploadComponent from '../../components/FileUploadComponent';
import DataTypeDetector from '../../components/DataTypeDetector';
import FieldMappingInterface from '../../components/FieldMappingInterface';
import ImportPreview from '../../components/ImportPreview';
import { apiClient } from '../../config/api';

const IMPORT_TARGETS = {
  master_checklist: {
    name: 'Master Checklist Items',
    description: 'Checklist items for onboarding processes',
    requiredFields: ['category', 'task', 'type'],
    optionalFields: ['field_type', 'info', 'display_order'],
    table: 'master_checklist_items'
  },
  contact_messages: {
    name: 'Contact Messages',
    description: 'Contact plan message templates',
    requiredFields: ['message_name', 'message_type'],
    optionalFields: ['message_content', 'notes', 'default_send_when', 'default_feed'],
    table: 'contact_messages'
  },
  consolidated_dealer_locations: {
    name: 'Clients & Dealer Locations (Complete)',
    description: 'Import clients/groups and dealer locations with automatic duplicate detection. Existing records with matching names will be updated with new data. Supports company/group info, locations, addresses, franchises, and account managers.',
    requiredFields: ['group_name', 'location_name'],
    optionalFields: [
      'company_name', 'contact_email', 'contact_phone', 'address',
      'account_manager_email', 'account_executive_email',
      'trading_name', 'marketing_contact', 'site_phone_number',
      'location_address', 'rooftops', 'franchises', 'franchise',
      'contract_expiry', 'leadbox_enabled',
      'brand', 'brands',
      'building_name', 'building_number', 'street_name', 'locality', 'town_city', 'county', 'postcode', 'country', 'address_type'
    ],
    table: 'dealer_locations',
    isConsolidated: true,
    isDynamic: true // Templates will be generated dynamically
  },
  links: {
    name: 'Admin Links',
    description: 'Link definitions for sales and service',
    requiredFields: ['link_key', 'link_label', 'link_type'],
    optionalFields: ['display_order', 'is_active', 'is_required'],
    table: 'links'
  },
  dropdown_options: {
    name: 'Dropdown Options',
    description: 'Options for various dropdown fields',
    requiredFields: ['option_type', 'option_value'],
    optionalFields: ['display_order', 'is_active'],
    table: 'dropdown_options'
  }
};

const IMPORT_STEPS = {
  UPLOAD: 'upload',
  DETECT_TYPES: 'detect_types',
  MAP_FIELDS: 'map_fields',
  PREVIEW: 'preview',
  IMPORT: 'import',
  COMPLETE: 'complete'
};

const AdminDataImportsPage = () => {
  const [currentStep, setCurrentStep] = useState(IMPORT_STEPS.UPLOAD);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [columnTypes, setColumnTypes] = useState({});
  const [fieldMappings, setFieldMappings] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dynamicTargetConfig, setDynamicTargetConfig] = useState(null);
  const [loadingDynamicConfig, setLoadingDynamicConfig] = useState(false);

  const resetImport = useCallback(() => {
    setCurrentStep(IMPORT_STEPS.UPLOAD);
    setSelectedTarget(null);
    setUploadedFile(null);
    setParsedData(null);
    setColumnTypes({});
    setFieldMappings({});
    setImportResult(null);
    setError(null);
    setIsProcessing(false);
    setDynamicTargetConfig(null);
    setLoadingDynamicConfig(false);
  }, []);

  const handleFileUpload = useCallback(async (file, target, data) => {
    try {
      setError(null);
      setUploadedFile(file);
      setSelectedTarget(target);
      setParsedData(data);
      
      // Fetch dynamic schema if target is dynamic
      const targetConfig = IMPORT_TARGETS[target];
      if (targetConfig && targetConfig.isDynamic) {
        setLoadingDynamicConfig(true);
        try {
          console.log('AdminDataImportsPage: Fetching dynamic schema for target:', target);
          const schema = await apiClient.get(`/admin/data-import/schema/${target}`);
          
          console.log('AdminDataImportsPage: Fetched dynamic schema', {
            target,
            requiredFields: schema.requiredFields?.length || 0,
            optionalFields: schema.optionalFields?.length || 0,
            linkFields: schema.linkFields?.length || 0,
            factfindFields: schema.factfindFields?.length || 0,
            sampleOptionalFields: schema.optionalFields?.slice(0, 10),
            allOptionalFields: schema.optionalFields
          });
          setDynamicTargetConfig(schema);
        } catch (err) {
          console.error('Error fetching dynamic schema:', err);
          console.error('Error details:', {
            message: err.message,
            response: err.response,
            status: err.status
          });
          // Fallback to static config
          setDynamicTargetConfig(null);
        } finally {
          setLoadingDynamicConfig(false);
        }
      } else {
        setDynamicTargetConfig(null);
        setLoadingDynamicConfig(false);
      }
      
      setCurrentStep(IMPORT_STEPS.DETECT_TYPES);
    } catch (err) {
      setError(`Error processing file: ${err.message}`);
    }
  }, []);

  const handleTypesDetected = useCallback((types) => {
    setColumnTypes(types);
    setCurrentStep(IMPORT_STEPS.MAP_FIELDS);
  }, []);
  
  // Auto-advance to mapping when dynamic config loads (if we're on DETECT_TYPES step)
  useEffect(() => {
    if (currentStep === IMPORT_STEPS.DETECT_TYPES && !loadingDynamicConfig) {
      const targetConfig = IMPORT_TARGETS[selectedTarget];
      if (targetConfig?.isDynamic) {
        // Config should be loaded by now, or we're using static fallback
        // The mapping step will handle showing loading if needed
      }
    }
  }, [currentStep, loadingDynamicConfig, selectedTarget, dynamicTargetConfig]);

  const handleFieldMappingsComplete = useCallback((mappings) => {
    setFieldMappings(mappings);
    setCurrentStep(IMPORT_STEPS.PREVIEW);
  }, []);

  const handleImportConfirm = useCallback(async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const importData = {
        target: selectedTarget,
        file: uploadedFile.name,
        data: parsedData,
        columnTypes,
        fieldMappings
      };

      const result = await apiClient.post('/admin/data-import', importData);
      setImportResult(result);
      setCurrentStep(IMPORT_STEPS.COMPLETE);
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedTarget, uploadedFile, parsedData, columnTypes, fieldMappings]);

  const downloadTemplate = useCallback(async (targetKey, format = 'csv') => {
    const target = IMPORT_TARGETS[targetKey];
    let headers = [...target.requiredFields, ...target.optionalFields];

    // For dynamic templates, fetch current schema from API
    if (target.isDynamic) {
      try {
        const schema = await apiClient.get(`/admin/data-import/schema/${targetKey}`);
        headers = [...schema.requiredFields, ...schema.optionalFields];
      } catch (error) {
        console.error('Error fetching dynamic template:', error);
        // Fall back to static headers
      }
    }

    if (format === 'csv') {
      // Create CSV content
      const csvContent = headers.join(',') + '\n';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${target.name.toLowerCase().replace(/\s+/g, '_')}_template.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (format === 'excel') {
      // Create Excel content using simple HTML table format that Excel can read
      const excelContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Template</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
        <body>
        <table>
        <tr>${headers.map(header => `<th>${header}</th>`).join('')}</tr>
        </table>
        </body>
        </html>
      `;
      const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${target.name.toLowerCase().replace(/\s+/g, '_')}_template.xls`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  const renderStepIndicator = () => {
    const steps = [
      { key: IMPORT_STEPS.UPLOAD, label: 'Upload File', icon: Upload },
      { key: IMPORT_STEPS.DETECT_TYPES, label: 'Detect Types', icon: Database },
      { key: IMPORT_STEPS.MAP_FIELDS, label: 'Map Fields', icon: Settings },
      { key: IMPORT_STEPS.PREVIEW, label: 'Preview', icon: FileText },
      { key: IMPORT_STEPS.IMPORT, label: 'Import', icon: CheckCircle }
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.key;
            const isCompleted = Object.values(IMPORT_STEPS).indexOf(currentStep) > Object.values(IMPORT_STEPS).indexOf(step.key);

            return (
              <React.Fragment key={step.key}>
                <div className={`flex flex-col items-center ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    isActive ? 'border-blue-600 bg-blue-50' :
                    isCompleted ? 'border-green-600 bg-green-50' :
                    'border-gray-300 bg-gray-50'
                  }`}>
                    <Icon size={20} />
                  </div>
                  <span className="text-sm mt-2 font-medium">{step.label}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${isCompleted ? 'bg-green-600' : 'bg-gray-300'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case IMPORT_STEPS.UPLOAD:
        return (
          <FileUploadComponent
            onFileUpload={handleFileUpload}
            importTargets={IMPORT_TARGETS}
          />
        );

      case IMPORT_STEPS.DETECT_TYPES:
        return (
          <DataTypeDetector
            data={parsedData}
            target={selectedTarget}
            onTypesDetected={handleTypesDetected}
            onBack={() => setCurrentStep(IMPORT_STEPS.UPLOAD)}
          />
        );

      case IMPORT_STEPS.MAP_FIELDS:
        // Wait for dynamic config to load if it's a dynamic target
        const targetConfigForMapping = IMPORT_TARGETS[selectedTarget];
        if (targetConfigForMapping?.isDynamic && loadingDynamicConfig) {
          return (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading field configuration...</p>
            </div>
          );
        }
        
        const targetConfig = dynamicTargetConfig || IMPORT_TARGETS[selectedTarget];
        console.log('AdminDataImportsPage: Rendering FieldMappingInterface', {
          usingDynamicConfig: !!dynamicTargetConfig,
          loadingDynamicConfig,
          targetConfig: {
            requiredFields: targetConfig.requiredFields?.length || 0,
            optionalFields: targetConfig.optionalFields?.length || 0,
            sampleOptionalFields: targetConfig.optionalFields?.slice(0, 10),
            allOptionalFields: targetConfig.optionalFields
          }
        });
        return (
          <FieldMappingInterface
            data={parsedData}
            columnTypes={columnTypes}
            target={selectedTarget}
            targetConfig={targetConfig}
            onMappingComplete={handleFieldMappingsComplete}
            onBack={() => setCurrentStep(IMPORT_STEPS.DETECT_TYPES)}
          />
        );

      case IMPORT_STEPS.PREVIEW:
        // Use dynamic config if available (for consolidated_dealer_locations), otherwise use static config
        const targetConfigForPreview = dynamicTargetConfig || IMPORT_TARGETS[selectedTarget];
        return (
          <ImportPreview
            data={parsedData}
            fieldMappings={fieldMappings}
            columnTypes={columnTypes}
            target={selectedTarget}
            targetConfig={targetConfigForPreview}
            onConfirm={handleImportConfirm}
            onBack={() => setCurrentStep(IMPORT_STEPS.MAP_FIELDS)}
            isProcessing={isProcessing}
          />
        );

      case IMPORT_STEPS.COMPLETE:
        return (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Complete!</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
              {importResult && (
                <div className="text-left">
                  <p className="text-green-800 mb-2">
                    <strong>Successfully imported:</strong> {importResult.imported} rows
                  </p>
                  {importResult.updated > 0 && (
                    <p className="text-blue-700 mb-2">
                      <strong>Updated existing:</strong> {importResult.updated} rows
                    </p>
                  )}
                  {importResult.skipped > 0 && (
                    <p className="text-yellow-700 mb-2">
                      <strong>Skipped:</strong> {importResult.skipped} rows
                    </p>
                  )}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="text-red-700">
                      <strong>Errors:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {importResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={resetImport}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium"
            >
              Import Another File
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Imports</h1>
          <p className="text-gray-600 mt-2">
            Import data from CSV or Excel files with intelligent field mapping and validation
          </p>
        </div>
        {currentStep !== IMPORT_STEPS.UPLOAD && (
          <button
            onClick={resetImport}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <X size={16} />
            Start Over
          </button>
        )}
      </div>

      {/* Template Downloads Section */}
      {currentStep === IMPORT_STEPS.UPLOAD && (
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Download className="text-blue-600" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-blue-900">Download Import Templates</h3>
              <p className="text-sm text-blue-700">
                Get properly formatted templates for each import target to ensure your data is structured correctly
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(IMPORT_TARGETS).map(([key, target]) => (
              <div key={key} className="bg-white border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{target.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{target.description}</p>
                <div className="text-xs text-gray-500 mb-3">
                  <div className="mb-1">
                    <span className="font-medium text-red-600">Required:</span> {target.requiredFields.join(', ')}
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Optional:</span> {target.optionalFields.slice(0, 2).join(', ')}
                    {target.optionalFields.length > 2 && '...'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadTemplate(key, 'csv')}
                    className="flex-1 text-xs bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition-colors"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => downloadTemplate(key, 'excel')}
                    className="flex-1 text-xs bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 transition-colors"
                  >
                    Excel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-600" size={20} />
          <div>
            <h4 className="text-red-800 font-medium">Error</h4>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {renderStepIndicator()}
      {renderCurrentStep()}
    </div>
  );
};

export default AdminDataImportsPage;